import {
	normalizeCollectionSort,
	normalizeMediaFilters,
	normalizeRatingFilters,
	normalizeStatusFilters,
} from '../../../shared/filters/collectionFilters'
import type { CollectionSortFilterOptions } from '../../../shared/types/collection'
import type { ClientUser, Settings } from '../../../shared/types/user'
import {
	DEFAULT_COLLECTION_FILTERS_STATE,
	type FiltersState,
} from '../../components/filters/filterState'
import { createFilterStore } from '../../components/filters/filterStore'
import {
	cloneFilterValue,
	normalizeDefaultFolderIds,
	normalizeDefaultStrings,
} from '../../components/filters/filterValues'

export type CollectionFiltersState = FiltersState &
	Required<Pick<FiltersState, 'dimReleases' | 'sort' | 'status'>>

export const collectionFilters = createFilterStore<CollectionFiltersState>({
	...DEFAULT_COLLECTION_FILTERS_STATE,
})

export function buildUserDefaultCollectionFilters(
	user: ClientUser,
): CollectionFiltersState {
	const userDefaultSort = user.settings?.defaultCollectionSort as
		| CollectionSortFilterOptions
		| undefined
	const initialSort = normalizeCollectionSort(userDefaultSort)

	return {
		...DEFAULT_COLLECTION_FILTERS_STATE,
		sort: initialSort,
		media: normalizeMediaFilters(user.settings?.defaultMedia),
		rating: normalizeRatingFilters(user.settings?.defaultRating),
		folderId: normalizeDefaultFolderIds(user.settings?.defaultFolderId),
		status: normalizeStatusFilters(user.settings?.defaultHistory),
		dimReleases: !user.settings?.dimReleaseIfHiddenOff,
		genre: normalizeDefaultStrings(user.settings?.defaultGenre),
		style: normalizeDefaultStrings(user.settings?.defaultStyle),
		yearFrom:
			user.settings?.defaultYearFrom ??
			DEFAULT_COLLECTION_FILTERS_STATE.yearFrom,
		yearTo:
			user.settings?.defaultYearTo ??
			DEFAULT_COLLECTION_FILTERS_STATE.yearTo,
	}
}

export function applyCollectionFilterDefaultsToSettings(
	settings: Settings,
	filters: CollectionFiltersState,
): Settings {
	return {
		...settings,
		defaultCollectionSort: filters.sort,
		defaultFolderId: cloneFilterValue(filters.folderId),
		defaultGenre: cloneFilterValue(filters.genre) ?? null,
		defaultHistory: cloneFilterValue(filters.status),
		defaultMedia: cloneFilterValue(filters.media),
		defaultRating: cloneFilterValue(filters.rating) ?? null,
		defaultStyle: cloneFilterValue(filters.style) ?? null,
		defaultYearFrom: filters.yearFrom ?? null,
		defaultYearTo: filters.yearTo ?? null,
		dimReleaseIfHiddenOff: !filters.dimReleases,
	}
}

export function buildCollectionFilterResetState(
	defaultFilters: CollectionFiltersState,
): Partial<FiltersState> {
	return {
		favoritesOnly: false,
		folderId: defaultFilters.folderId,
		genre: defaultFilters.genre,
		media: defaultFilters.media,
		rating: defaultFilters.rating,
		status: defaultFilters.status,
		style: defaultFilters.style,
		yearFrom: defaultFilters.yearFrom,
		yearTo: defaultFilters.yearTo,
	}
}
